import { useTranslation } from "react-i18next"
import { CircleHelp } from "lucide-react"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"

import {
  getFirstPasswordBasicError,
  PASSWORD_MIN_LENGTH,
} from "@/lib/passwordValidation"

export interface PasswordBasicErrorMessageProps {
  password: string
  show?: boolean
}

export const PasswordBasicErrorMessage = ({
  password,
  show = true,
}: PasswordBasicErrorMessageProps) => {
  const { t } = useTranslation()
  const firstError = getFirstPasswordBasicError(password)

  if (!show || !firstError) return null

  return (
    <p className="text-xs text-red-600">
      {t(`common.auth.passwordRequirements.errors.${firstError}`, {
        count: PASSWORD_MIN_LENGTH,
      })}
    </p>
  )
}

const PasswordRequirements = () => {
  const { t } = useTranslation()

  const checkableRequirements = [
    {
      key: "minLength",
      label: t("common.auth.passwordRequirements.minLength", {
        count: PASSWORD_MIN_LENGTH,
      }),
    },
    {
      key: "notNumeric",
      label: t("common.auth.passwordRequirements.notNumeric"),
    },
  ]

  const serverValidatedRequirements = [
    t("common.auth.passwordRequirements.notCommon"),
  ]

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t("common.auth.passwordRequirements.title")}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-primary hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <CircleHelp className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        sideOffset={6}
        className="max-w-xs text-left"
      >
        <div className="font-medium">
          {t("common.auth.passwordRequirements.title")}
        </div>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          {checkableRequirements.map((requirement) => (
            <li key={requirement.key}>{requirement.label}</li>
          ))}
          {serverValidatedRequirements.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}

export default PasswordRequirements
